import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {format, isSameWeek} from 'date-fns';
import {classNames} from '../support/classnames';
import {isFullyUnavailable} from '../support/is_fully_unavailable';
import {SprintBlock} from './sprint_block';
import {AvailabilityIndicator} from './availability_indicator';
import {PresenterProps, withPresenter} from '../support/with_presenter';
import {Employee} from '../../models/employee';
import {Sprint} from '../../models/sprint';
import {SprintCellPresenter} from './sprint_cell_presenter';
import {Availability} from '../../models/availability';
import {SprintMover} from '../../business/sprint_mover';
import {SprintAddButton} from './sprint_add_button';
import {SprintCreater} from '../../business/sprint_creater';
import * as Modal from 'react-modal';

interface OwnProps {
    now: Date;
    compact: boolean;
    date: Date;
    sprints: Sprint[];
    employee: Employee;
    employeeAvailabilities: {[date: string]: Availability};
}

const SprintCellComponent: FunctionComponent<OwnProps & PresenterProps<SprintCellPresenter>> = observer(
    ({presenter, now, date, employee, sprints, employeeAvailabilities, compact}) => {
        const formattedDate = format(date, 'yyyy-MM-dd');
        const sprintsForCell = sprints.filter(
            (s) => s.employee_id === employee.id && format(new Date(s.date), 'yyyy-MM-dd') === formattedDate,
        );
        const availability: Availability | undefined = employeeAvailabilities[formattedDate];
        return (
            <td
                key={formattedDate}
                onDrop={() => presenter.handleDrop()}
                onDragOver={(event) => presenter.handleDragOver(event)}
                onDragLeave={() => presenter.handleDragLeave()}
                className={classNames({
                    'fully-unavailable': availability !== undefined && isFullyUnavailable(availability),
                    active: isSameWeek(now, date),
                    'is-selected': presenter.isSelected,
                })}
            >
                {sprintsForCell.map((sprint) => (
                    <SprintBlock key={sprint.id} sprint={sprint} employee={employee} compact={compact} />
                ))}
                <SprintAddButton date={date} employee={employee} />
                {!compact && (
                    <a
                        className="btn btn-icon availability ion-android-calendar"
                        href={`/availability/create?date=${formattedDate}&employee_id=${employee.id}`}
                    />
                )}
                {!compact && availability && <AvailabilityIndicator availability={availability} />}
                {presenter.modalVisible && ( //This ensures the modal is only mounted when actually requested
                    <Modal
                        isOpen={presenter.modalVisible}
                        onRequestClose={() => presenter.onRequestModalClose()}
                        contentLabel="Create sprint"
                        ariaHideApp={false}
                        style={{
                            content: {
                                top: '50%',
                                left: '50%',
                                right: 'auto',
                                bottom: 'auto',
                                marginRight: '-50%',
                                transform: 'translate(-50%, -50%)',
                                width: '600px',
                            },
                        }}
                    >
                        <div className="panel panel-default">
                            <div className="panel-heading">Create sprint</div>
                            <div className="panel-body panel-padding">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        presenter.onSubmit();
                                    }}
                                >
                                    <div className="form-group">
                                        <label htmlFor="input-name" className="col-sm-2 control-label">
                                            Employee
                                        </label>
                                        <div className="col-sm-10">{employee.name}</div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="input-state" className="col-sm-2 control-label">
                                            Estimate
                                        </label>
                                        <div className="col-sm-10">
                                            <select
                                                className="form-control "
                                                id="input-state"
                                                name="state"
                                                value={presenter.estimateId}
                                                onChange={(e) =>
                                                    presenter.onEstimateIdChange(parseInt(e.target.value, 10))
                                                }
                                            >
                                                {presenter.estimates.map((estimate) => (
                                                    <option key={estimate.id} value={estimate.id}>
                                                        {estimate.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="input-hours" className="col-sm-2 control-label">
                                            Hours
                                        </label>
                                        <div className="col-sm-10">
                                            <input
                                                className="form-control"
                                                id="input-hours"
                                                name="hours"
                                                type="number"
                                                value={presenter.hours}
                                                onChange={(e) => presenter.onHoursChange(parseInt(e.target.value, 10))}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="col-sm-2 control-label">Extra?</label>
                                        <div className="col-sm-10">
                                            <div className="checkbox">
                                                <input
                                                    id="input-is-extra"
                                                    checked={presenter.isExtra}
                                                    name="is_extra"
                                                    type="checkbox"
                                                    onChange={(e) => presenter.onIsExtraChange(e.target.checked)}
                                                />
                                                <label htmlFor="input-is-extra">Extra?</label>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <footer>
                                <a
                                    className="btn btn-inline btn-secondary ion-android-close pull-left"
                                    onClick={() => presenter.onRequestModalClose()}
                                >
                                    Close
                                </a>
                                {presenter.fromDate && (
                                    <a
                                        href={`/sprints/create?date=${format(
                                            presenter.fromDate,
                                            'yyyy-MM-dd',
                                        )}&employee_id=${employee.id}&till_date=${format(date, 'yyyy-MM-dd')}`}
                                        className="btn  btn-inline btn-secondary ion-chevron-right pull-left"
                                    >
                                        Legacy create
                                    </a>
                                )}
                                <button
                                    className="btn btn-default btn-inline ion-android-done pull-right"
                                    onClick={() => presenter.onSaveClick()}
                                >
                                    {presenter.saving ? 'Creating...' : 'Create'}
                                </button>
                            </footer>
                        </div>
                    </Modal>
                )}
            </td>
        );
    },
);

export const SprintCell = withPresenter<SprintCellPresenter, OwnProps>(
    (props, component) =>
        new SprintCellPresenter(
            props.date,
            props.employee.id,
            component.dragContext,
            new SprintMover(component.sprintsRepository, component.sprintApi),
            component.estimatesRepository,
            new SprintCreater(component.sprintApi, component.sprintsRepository),
        ),
    SprintCellComponent,
);
